import * as React from 'react';
import { cn } from '../lib/cn';

/**
 * UsageBar — 4px bar with tone shifting by percent.
 *   ≤50% green (success), ≤80% amber (warning), >80% red (danger).
 * Used in ServiceCard, the UsageBarCell table renderer, and ChartMeter.
 *
 * THIS LADDER IS THE ONE DEFINITION. `usageToneName` exists so a caller that
 * needs to colour something ELSE by the same rule — ChartMeter's percent
 * readout, for one — derives it here instead of restating the thresholds.
 * Two copies of a predicate is not a duplication risk, it IS the defect: the
 * AdminDashboard capacity ribbon carried its own ladder (amber at 60 rather
 * than 50) and disagreed with this file at 55% and 65% until 2026-08-19.
 *
 * A third copy still sits in portal/src/shared/utils/format.ts as `usageTone`
 * (amber at 70, red at 90) — it disagrees with this one at 55, 65, 85 and 88%,
 * but it has ZERO consumers. Delete it rather than reconcile it.
 */

type UsageTone = 'success' | 'warning' | 'danger';

interface UsageBarProps {
  value: number;
  max?: number;
  className?: string;
  ariaLabel?: string;
}

function usageToneName(percent: number): UsageTone {
  if (percent <= 50) return 'success';
  if (percent <= 80) return 'warning';
  return 'danger';
}

/** Literal class strings, not `bg-${tone}` — Tailwind cannot see a template. */
const BAR_TONE_CLASS: Record<UsageTone, string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
};

function toneFor(percent: number): string {
  return BAR_TONE_CLASS[usageToneName(percent)];
}

const UsageBar = React.forwardRef<HTMLDivElement, UsageBarProps>(
  ({ value, max = 100, className, ariaLabel }, ref) => {
    const safeMax = max > 0 ? max : 1;
    const percent = Math.min(100, Math.max(0, (value / safeMax) * 100));
    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuenow={Math.round(percent)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={ariaLabel}
        className={cn('h-1 w-full overflow-hidden rounded-full bg-muted', className)}
      >
        <div
          className={cn('h-full rounded-full transition-all duration-300', toneFor(percent))}
          style={{ width: `${percent}%` }}
        />
      </div>
    );
  }
);
UsageBar.displayName = 'UsageBar';

export { UsageBar, toneFor as usageBarToneFor, usageToneName };
export type { UsageBarProps, UsageTone };
